import React, { useCallback, useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { DesignTaskStatusBadge, StatusBadge } from "../components/etams/StatusBadge";
import { Input } from "../components/ui/input";
import { DR_STATUS_LABEL } from "../lib/statusLabels";
import { mintaTeks } from "../lib/dialogAplikasi";
import { apiErrorMessage } from "../lib/inventoryApi";
import { AlertCircle, CheckCircle2, Upload, FileCheck2 } from "lucide-react";
import BriefCustomer from "../components/etams/BriefCustomer";
import { labelPaket, labelProduk } from "../lib/faKonfigurasi";

const MAX_BYTES = 25 * 1024 * 1024;
// K206 butir 6: nama customer hanya untuk peran berhak isi FA (cermin `hak_baca_fa.PERAN_ISI_FA`).
const PERAN_NAMA_CUSTOMER = ["OWNER", "KEPALA_CABANG", "CS"];

// Q68-01 Batch 5. `TASK_STATUS_LABEL` DIHAPUS dari sini — salinan kata demi
// kata dari `DesignTasks.jsx:10`, dan satu-satunya pemakainya di berkas ini
// adalah badge yang sekarang memakai `DesignTaskStatusBadge` (labelnya ikut
// dari `DESIGN_TASK_STATUS_TONE`). Yang tersisa di DesignTasks.jsx bukan
// salinan ketiga: ia diturunkan dari peta tone itu juga.
//
// Badge "FINAL" di tabel berkas (`:200`) SENGAJA memakai `StatusBadge` generik
// dengan tone literal, BUKAN komponen berpeta. Alasannya bukan kemalasan:
// `design_files.is_final_selected` adalah kolom BOOLEAN
// (`004_phase1b_correction_flow.sql:327`), bukan anggota state machine —
// tidak ada daftar status untuk dipetakan, dan membuatkannya peta tone justru
// akan mengarang "status" yang tidak ada di DB. Yang diperbaiki di sini murni
// warnanya: kelas hijau literal → tone token, supaya ikut bertukar di
// mode gelap seperti badge lain di layar yang sama. Ini wilayah Q69 (sapuan
// token) yang kebetulan dilewati, bukan wilayah Q68 (penyatuan peta status).

export default function DesignTaskDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { profile } = useAuth();
  const isDesigner = profile?.role?.code === "DESIGNER_EDITOR";

  const [task, setTask] = useState(null);
  const [dr, setDr] = useState(null);
  const [customer, setCustomer] = useState(null);
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);
  const [actionErr, setActionErr] = useState(null);

  const [iterFile, setIterFile] = useState(null);
  const [finalFile, setFinalFile] = useState(null);
  const [spv, setSpv] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setErr(null);
    try {
      // ⛔ KELAS H: dulu task dicari di tiga daftar ber-plafon 200 (OPEN/CLAIMED/COMPLETED
      // TERTUA) — task di luar jendela itu "tidak ditemukan" padahal ada. Sekarang per id.
      let t = null;
      try {
        t = (await api.get(`/design-tasks/${id}`)).data;
      } catch (e) {
        if (e.response?.status !== 404) throw e;
      }
      if (!t) {
        setErr("Tugas desain tidak ditemukan, atau Anda tidak punya hak untuk melihatnya.");
        setLoading(false);
        return;
      }
      setTask(t);
      const dRes = await api.get(`/design-requests/${t.design_request_id}`);
      setDr(dRes.data);
      // T11 (26 Okt 2026): nama customer hanya untuk Owner/KC/CS (K206 butir 6, `hak_baca_fa.boleh_nama_customer`).
      // Desainer/SPV tidak memanggilnya sama sekali — dulu panggilan itu 403 lalu layar menulis "—".
      if (dRes.data?.customer_id && PERAN_NAMA_CUSTOMER.includes(profile?.role?.code)) {
        try { const c = await api.get(`/customers/${dRes.data.customer_id}`); setCustomer(c.data); } catch { /* */ }
      }
      try {
        const f = await api.get("/design-files", { params: { design_request_id: t.design_request_id } });
        setFiles(f.data?.items || []);
      } catch { setFiles([]); }
      // ⛔ P1 sesi 20 — status SPV dibaca dari SATU sumber dengan gerbang
      //    submit-final (`lolos_spv()`). Gerbang yang nol bisa dilihat
      //    sebelum ditabrak melatih orang menabrak gerbang: sebelum panel
      //    ini ada, desainer baru tahu keadaannya saat submit-final DITOLAK.
      try {
        const sv = await api.get(`/design-iterations/status-spv/${t.design_request_id}`);
        setSpv(sv.data);
      } catch { setSpv(null); }
    } catch (e) {
      setErr(apiErrorMessage(e, "Tugas desain gagal dimuat."));
    } finally {
      setLoading(false);
    }
  }, [id, profile?.role?.code]);

  useEffect(() => { load(); }, [load]);

  async function doClaim() {
    setBusy(true); setActionErr(null);
    try { await api.post(`/design-tasks/${id}/claim`); await load(); }
    catch (e) { setActionErr(apiErrorMessage(e, "Aksi gagal. Coba lagi.")); }
    finally { setBusy(false); }
  }

  async function uploadFile(file, isFinal) {
    setBusy(true); setActionErr(null);
    try {
      if (file.size > MAX_BYTES) throw new Error("Berkas lebih dari 25 MB.");
      const fd = new FormData();
      fd.append("file", file);
      fd.append("design_request_id", task.design_request_id);
      fd.append("is_final_selected", isFinal ? "true" : "false");
      await api.post("/design-files", fd, { headers: { "Content-Type": "multipart/form-data" } });
      if (isFinal) setFinalFile(null); else setIterFile(null);
      await load();
    } catch (e) {
      setActionErr(apiErrorMessage(e, "Aksi gagal. Coba lagi."));
    } finally { setBusy(false); }
  }

  async function viewSigned(fileId) {
    try { const r = await api.get(`/design-files/${fileId}/signed-url`); if (r.data?.signed_url) window.open(r.data.signed_url, "_blank", "noopener,noreferrer"); }
    catch (e) { setActionErr(apiErrorMessage(e, "Aksi gagal. Coba lagi.")); }
  }

  async function doKirimKeSpv() {
    setBusy(true); setActionErr(null);
    try {
      await api.post("/design-iterations/submit", { design_request_id: task.design_request_id });
      await load();
    } catch (e) {
      setActionErr(apiErrorMessage(e, "Aksi gagal. Coba lagi."));
    } finally { setBusy(false); }
  }

  // BUSINESS-41: batal task desain — hanya Kepala Cabang region pesanan; alasan wajib (backend menegakkan).
  async function doBatal() {
    const alasan = await mintaTeks("Alasan membatalkan tugas desain ini (PD tetap terbuka untuk tugas baru; jatah revisi customer tidak berkurang):", { minPanjang: 5 });
    if (!alasan) return;
    setBusy(true); setActionErr(null);
    try { await api.post(`/design-tasks/${id}/cancel`, { reason: alasan }); await load(); }
    catch (e) { setActionErr(apiErrorMessage(e, "Aksi gagal. Coba lagi.")); }
    finally { setBusy(false); }
  }

  async function doSubmitFinal() {
    setBusy(true); setActionErr(null);
    try {
      const r = await api.post(`/design-tasks/${id}/submit-final`);
      // K56 poin 1: lolos SPV belum berarti disetujui customer — DR menunggu keputusan customer.
      if (r.data?.menunggu_keputusan_customer) {
        setActionErr("Tugas selesai. Desain menunggu keputusan customer — CS mengirimkannya; FA baru bisa dibuat sesudah customer setuju.");
      }
      await load();
    } catch (e) {
      setActionErr(apiErrorMessage(e, "Aksi gagal. Coba lagi."));
    } finally { setBusy(false); }
  }

  if (loading) return <div className="p-6 text-sm text-muted-foreground" data-testid="design-task-loading">Memuat...</div>;
  if (err) return <div className="p-6 text-sm text-danger-soft-foreground bg-danger-soft border border-danger-soft-border rounded" data-testid="design-task-error">{err}</div>;
  if (!task) return null;

  const myTask = task.claimed_by === profile?.id;
  const canClaim = isDesigner && task.status === "OPEN";
  const canUpload = isDesigner && myTask && task.status === "CLAIMED";
  const finalCount = files.filter((f) => f.is_final_selected).length;
  // T51 (26 Okt 2026): KIRIMAN = baris kiriman ke SPV (status-spv), BERKAS = isi tabel di bawah. Dulu jumlah
  // berkas ditulis "iterasi", padahal berkas perbaikan menempel ke kiriman yang sama (K56 poin 2).
  const kiriman = spv?.kiriman || [];
  const nomorKiriman = Object.fromEntries(kiriman.map((k) => [k.id, k.iteration_number]));
  // T12: desain final hanya sesudah customer menyetujui (backend submit-final menolak sebelum itu, K56 poin 1).
  const customerSetuju = !!spv?.customer_setuju;
  const canSubmitFinal = canUpload && customerSetuju && finalCount > 0;
  const canBatal = profile?.role?.code === "KEPALA_CABANG" && ["OPEN", "CLAIMED"].includes(task.status);

  return (
    <div className="max-w-4xl space-y-6" data-testid="design-task-detail">
      <div className="flex items-start justify-between">
        <div>
          <div className="text-sm text-muted-foreground">Tugas Desainer</div>
          <h1 className="text-2xl font-bold" data-testid="design-task-dr-number">{dr?.dr_number}</h1>
          <div className="text-sm text-muted-foreground mt-1" data-testid="design-task-ringkas">
            {[customer?.name, dr?.product_summary_type && labelProduk(dr.product_summary_type),
              dr?.package_type && `Paket ${labelPaket(dr.package_type)}`].filter(Boolean).join(" · ")}
          </div>
        </div>
        <DesignTaskStatusBadge value={task.status} />
      </div>

      {actionErr && (
        <div className="flex items-start gap-2 p-3 rounded border border-danger-soft-border bg-danger-soft text-danger-soft-foreground text-sm" data-testid="design-task-action-error">
          <AlertCircle size={16} className="mt-0.5 shrink-0" /><span className="break-all">{actionErr}</span>
        </div>
      )}

      {/* T10 (PRIORITAS TINGGI, 26 Okt 2026): permintaan customer dari Ruang Pesanan — dasar kerja desainer. */}
      <Card className="p-6 space-y-3" data-testid="design-task-brief">
        <h2 className="font-semibold">Permintaan customer</h2>
        <BriefCustomer drId={dr?.id} />
      </Card>

      <Card className="p-6 space-y-3">
        <h2 className="font-semibold">Detail Permintaan Desain</h2>
        <div className="grid grid-cols-2 gap-3 text-sm">
          <div><span className="text-muted-foreground">Status PD:</span> {DR_STATUS_LABEL[dr?.dr_status] || "—"}</div>
          {/* T11b: angka PD = perkiraan saat pesanan dibuat; jumlah yang dikonfirmasi customer ada di kartu atas. */}
          <div><span className="text-muted-foreground">Perkiraan awal atasan:</span> {dr?.preliminary_qty_atasan ?? 0}</div>
          <div><span className="text-muted-foreground">Perkiraan awal celana:</span> {dr?.preliminary_qty_celana || 0}</div>
          <div><span className="text-muted-foreground">Pesanan ulang:</span> {dr?.is_repeat_order ? "Ya" : "Tidak"}</div>
          {dr?.notes && (
            <div className="col-span-2"><span className="text-muted-foreground">Catatan pesanan:</span> {dr.notes}</div>
          )}
        </div>
      </Card>

      {canBatal && (
        <div className="flex justify-end">
          <Button variant="outline" size="sm" onClick={doBatal} disabled={busy} data-testid="design-task-batal-btn">
            Batalkan tugas desain
          </Button>
        </div>
      )}

      {canClaim && (
        <Card className="p-6">
          <Button onClick={doClaim} disabled={busy} data-testid="design-task-claim-btn">
            {busy ? "Memproses..." : "Ambil Tugas (mulai kerjakan)"}
          </Button>
        </Card>
      )}

      {(task.status === "CLAIMED" || task.status === "COMPLETED") && (
        <Card className="p-6 space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="font-semibold">Berkas Desain</h2>
            <div className="text-xs text-muted-foreground" data-testid="design-task-hitungan">
              {kiriman.length} kiriman ke SPV · {files.length} berkas{finalCount > 0 ? ` · ${finalCount} final` : ""}
            </div>
          </div>

          {canUpload && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {!customerSetuju && (
              <div className="border border-dashed border-border rounded p-3 bg-muted/50" data-testid="design-iter-box">
                <div className="text-xs font-medium mb-2 flex items-center gap-1"><Upload size={12} />Unggah berkas desain</div>
                <p className="text-xs text-muted-foreground mb-2">Berkas ikut kiriman berikutnya ke SPV.</p>
                <input type="file" onChange={(e) => setIterFile(e.target.files?.[0] || null)} className="text-xs w-full" data-testid="design-iter-input" />
                {iterFile && (
                  <Button size="sm" className="mt-2 w-full" onClick={() => uploadFile(iterFile, false)} disabled={busy} data-testid="design-iter-upload-btn">
                    {busy ? "Mengunggah..." : `Unggah berkas (${(iterFile.size / 1024).toFixed(1)} KB)`}
                  </Button>
                )}
              </div>
              )}
              {customerSetuju && (
              <div className="border border-dashed border-success-soft-border rounded p-3 bg-success-soft" data-testid="design-final-box">
                <div className="text-xs font-medium mb-2 flex items-center gap-1"><FileCheck2 size={12} />Unggah Desain Final</div>
                <input type="file" onChange={(e) => setFinalFile(e.target.files?.[0] || null)} className="text-xs w-full" data-testid="design-final-input" />
                {finalFile && (
                  <Button size="sm" className="mt-2 w-full bg-success text-success-foreground hover:bg-success/90" onClick={() => uploadFile(finalFile, true)} disabled={busy} data-testid="design-final-upload-btn">
                    {busy ? "Mengunggah..." : `Unggah Final (${(finalFile.size / 1024).toFixed(1)} KB)`}
                  </Button>
                )}
              </div>
              )}
            </div>
          )}

          {files.length === 0 ? (
            <p className="text-xs text-muted-foreground italic">Belum ada berkas diunggah.</p>
          ) : (
            <table className="w-full text-sm" data-testid="design-files-table">
              <thead className="text-xs text-muted-foreground border-b">
                <tr><th className="text-left py-2">Nama berkas</th><th className="text-left">Kiriman</th><th className="text-right">Ukuran</th><th>Final?</th><th></th></tr>
              </thead>
              <tbody>
                {files.map((f) => (
                  <tr key={f.id} className="border-b border-border">
                    <td className="py-2 truncate max-w-xs">{f.file_filename}</td>
                    <td className="text-xs text-muted-foreground">
                      {f.iteration_id && nomorKiriman[f.iteration_id] ? `ke-${nomorKiriman[f.iteration_id]}` : "belum dikirim"}
                    </td>
                    <td className="text-right text-xs">{((f.file_size_bytes || 0) / 1024).toFixed(1)} KB</td>
                    <td>{f.is_final_selected ? <StatusBadge tone="success">FINAL</StatusBadge> : <span className="text-xs text-muted-foreground">desain</span>}</td>
                    <td className="text-right"><button onClick={() => viewSigned(f.id)} className="text-xs text-info hover:underline">Buka</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          {/* ⛔ GERBANG SPV (K55) — P1 sesi 20. Empat keadaan, empat tampilan
              BERBEDA, karena keempatnya menuntut tindakan berbeda: belum
              kirim -> kirim; menunggu -> tagih SPV; ditolak -> baca masukan,
              perbaiki, kirim ulang; lolos -> submit final terbuka. Panel yang
              cuma bilang "belum lolos SPV" nol memberi tahu yang mana. */}
          {spv && (canUpload || task.status === "COMPLETED") && (
            <div className="border-t pt-4 space-y-2" data-testid="design-spv-panel">
              <div className={
                spv.lolos
                  ? "rounded border border-success-soft-border bg-success-soft p-3 text-sm text-success-soft-foreground"
                  : spv.kode === "SPV_MENOLAK"
                    ? "rounded border border-danger-soft-border bg-danger-soft p-3 text-sm text-danger-soft-foreground"
                    : "rounded border border-warning-soft-border bg-warning-soft p-3 text-sm text-warning-soft-foreground"
              }>
                <span className="font-medium">
                  {spv.lolos ? (customerSetuju ? "Disetujui customer" : "Lolos SPV — menunggu keputusan customer") :
                   spv.kode === "SPV_MENOLAK" ? "Ditolak SPV" :
                   spv.kode === "CUSTOMER_MINTA_REVISI" ? "Customer minta perbaikan" :
                   spv.kode === "SPV_MENUNGGU_VONIS" ? "Menunggu persetujuan SPV" :
                   "Belum dikirim ke SPV"}
                </span>
                <span className="block mt-1">{spv.pesan}</span>
              </div>
              {canUpload && !spv.lolos && spv.kode !== "SPV_MENUNGGU_VONIS" && (
                <div>
                  <Button onClick={doKirimKeSpv} disabled={busy || files.length === 0}
                          data-testid="design-task-kirim-spv-btn">
                    {busy ? "Mengirim..." :
                     spv.kode === "SPV_MENOLAK" ? "Kirim ULANG ke SPV (perbaikan)" :
                     spv.kode === "CUSTOMER_MINTA_REVISI" ? "Kirim perbaikan ke SPV" : "Kirim ke SPV"}
                  </Button>
                  {files.length === 0 && (
                    <p className="text-xs text-muted-foreground mt-1">Unggah minimal satu berkas dulu — kiriman kosong tidak bisa dinilai SPV.</p>
                  )}
                </div>
              )}
            </div>
          )}

          {canSubmitFinal && (
            <div className="border-t pt-4">
              <Button onClick={doSubmitFinal} disabled={busy || (spv && !spv.lolos)} className="bg-success text-success-foreground hover:bg-success/90" data-testid="design-task-submit-final-btn">
                <CheckCircle2 size={14} className="mr-1" />{busy ? "Mengirim..." : "Kirim Final → PD Desain Final Disetujui"}
              </Button>
              {spv && !spv.lolos ? (
                <p className="text-xs text-warning-soft-foreground mt-2">Terkunci sampai SPV meloloskan kiriman terakhir.</p>
              ) : (
                <p className="text-xs text-muted-foreground mt-2">PD akan berstatus "Desain Final Disetujui" dan CS bisa lanjut buat FA.</p>
              )}
            </div>
          )}
          {canUpload && customerSetuju && !canSubmitFinal && (
            <p className="text-xs text-muted-foreground italic">Unggah minimal 1 berkas final untuk bisa Kirim Final.</p>
          )}
        </Card>
      )}

      <Button variant="ghost" onClick={() => navigate("/design-tasks")}>← Kembali ke daftar</Button>
    </div>
  );
}
